/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import { IAdminClozeData } from '@datatype/admin/api-type';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import TextAudioButton from '@design-system/text-audio-button';
import ClozeReviewModal from '@component/admin/cloze/cloze-home/cloze-preview/cloze-review-modal';

interface IProps {
  selectedCloze: IAdminClozeData | null;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  padding: 20px;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_3};
  border-radius: 8px;
  min-height: 200px;
`;

const TopBar = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
`;

const CenterSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 4px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;
  gap: 12px;
`;

const FieldLabel = styled.div`
  flex: 0 0 180px;
  text-align: right;
`;

const FieldValue = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Empty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 200px;
`;

const Section = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  padding: 16px;
  background-color: ${color.GRAY_5};
  border: 1px solid ${color.GRAY_3};
  border-radius: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed ${color.GRAY_3};
`;

const SectionHeaderRight = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
`;

const LanguageBadge = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 2px 8px;
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.BROWN_1};
`;

const OtherLangBlock = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  padding: 12px;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_3};
  border-radius: 6px;
`;

const KeywordRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${size.MARGIN_1};
  justify-content: center;
  align-items: center;
`;

const KeywordChip = styled.div`
  display: inline-flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  gap: 6px;
  padding: 8px 12px;
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_3};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
`;

const KeywordChipMainRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
`;

const KeywordChipSubRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 6px;
`;

const KeywordChipLabel = styled.span`
  padding: 2px 8px;
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.BROWN_1};
  color: ${color.WHITE_1};
  font-size: 12px;
  font-weight: 600;
`;

const ClozePreview: React.FC<IProps> = ({ selectedCloze }) => {
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  if (!selectedCloze) {
    return (
      <Wrapper>
        <Empty>
          <TextBlock text='문제를 선택하세요' design={{ text: { size: 'medium', color: color.GRAY_2 } }} />
        </Empty>
      </Wrapper>
    );
  }

  const lc = selectedCloze.languageCode?.toUpperCase();
  const sentenceTemplate =
    lc === 'EN'
      ? selectedCloze.sentenceTemplateEn
      : lc === 'JA'
        ? selectedCloze.sentenceTemplateJa
        : selectedCloze.sentenceTemplateKo;
  const sentence =
    lc === 'EN' ? selectedCloze.sentenceEn : lc === 'JA' ? selectedCloze.sentenceJa : selectedCloze.sentenceKo;
  const choices = lc === 'EN' ? selectedCloze.choiceEn : lc === 'JA' ? selectedCloze.choiceJa : selectedCloze.choiceKo;
  const answers = lc === 'EN' ? selectedCloze.answerEn : lc === 'JA' ? selectedCloze.answerJa : selectedCloze.answerKo;
  const sentenceAudioLink =
    lc === 'EN'
      ? selectedCloze.sentenceAudioLinkEn
      : lc === 'JA'
        ? selectedCloze.sentenceAudioLinkJa
        : selectedCloze.sentenceAudioLinkKo;
  const sourceKeyword = selectedCloze.sourceKeyword;

  const otherLanguages: {
    label: string;
    sentenceTemplate: string;
    sentence: string;
    choices: string[];
    answers: string[];
  }[] = [
    {
      label: 'KO',
      sentenceTemplate: selectedCloze.sentenceTemplateKo,
      sentence: selectedCloze.sentenceKo,
      choices: selectedCloze.choiceKo,
      answers: selectedCloze.answerKo,
    },
    {
      label: 'EN',
      sentenceTemplate: selectedCloze.sentenceTemplateEn,
      sentence: selectedCloze.sentenceEn,
      choices: selectedCloze.choiceEn,
      answers: selectedCloze.answerEn,
    },
    {
      label: 'JA',
      sentenceTemplate: selectedCloze.sentenceTemplateJa,
      sentence: selectedCloze.sentenceJa,
      choices: selectedCloze.choiceJa,
      answers: selectedCloze.answerJa,
    },
  ].filter((item) => item.label !== lc);

  return (
    <Wrapper>
      <TopBar>
        <TextButton
          text='검수결과 보기'
          theme={ETheme.GRAY}
          kind='outlined'
          width='160px'
          handleClick={() => setIsReviewOpen(true)}
        />
      </TopBar>

      <Section>
        <SectionHeader>
          <LanguageBadge>
            <TextBlock text='키워드' design={{ text: { size: 'small', color: color.WHITE_1, bold: true } }} />
          </LanguageBadge>
          {selectedCloze.sourceKeywordAudioLink && (
            <SectionHeaderRight>
              <TextBlock
                text='원본 키워드'
                design={{ text: { size: 'small', color: color.BLACK_4, bold: true } }}
              />
              <TextAudioButton
                sources={[selectedCloze.sourceKeywordAudioLink]}
                size='small'
                kind='outlined'
                shape='round'
                width='72px'
              />
            </SectionHeaderRight>
          )}
        </SectionHeader>

        <KeywordRow>
          {sourceKeyword && (
            <KeywordChip>
              <KeywordChipMainRow>
                <KeywordChipLabel>원본</KeywordChipLabel>
                <TextBlock
                  text={sourceKeyword}
                  design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
                />
              </KeywordChipMainRow>
              <KeywordChipSubRow>
                <TextBlock
                  text={selectedCloze.sourceKeywordPronunciation ? `[${selectedCloze.sourceKeywordPronunciation}]` : ' '}
                  design={{ text: { size: 'small', color: color.BLACK_4 } }}
                />
              </KeywordChipSubRow>
            </KeywordChip>
          )}
          {selectedCloze.keywordMeaningKo && (
            <KeywordChip>
              <KeywordChipMainRow>
                <KeywordChipLabel>한국어</KeywordChipLabel>
                <TextBlock
                  text={selectedCloze.keywordMeaningKo}
                  design={{ text: { size: 'large', color: color.BLACK_2 } }}
                />
              </KeywordChipMainRow>
              <KeywordChipSubRow>
                <TextBlock text=' ' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
              </KeywordChipSubRow>
            </KeywordChip>
          )}
          {selectedCloze.keywordMeaningEn && (
            <KeywordChip>
              <KeywordChipMainRow>
                <KeywordChipLabel>영어</KeywordChipLabel>
                <TextBlock
                  text={selectedCloze.keywordMeaningEn}
                  design={{ text: { size: 'large', color: color.BLACK_2 } }}
                />
              </KeywordChipMainRow>
              <KeywordChipSubRow>
                <TextBlock text=' ' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
              </KeywordChipSubRow>
            </KeywordChip>
          )}
          {selectedCloze.keywordMeaningJa && (
            <KeywordChip>
              <KeywordChipMainRow>
                <KeywordChipLabel>일본어</KeywordChipLabel>
                <TextBlock
                  text={selectedCloze.keywordMeaningJa}
                  design={{ text: { size: 'large', color: color.BLACK_2 } }}
                />
              </KeywordChipMainRow>
              <KeywordChipSubRow>
                <TextBlock text=' ' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
              </KeywordChipSubRow>
            </KeywordChip>
          )}
        </KeywordRow>
      </Section>

      <Section>
        <SectionHeader>
          <LanguageBadge>
            <TextBlock text={`원본 · ${lc}`} design={{ text: { size: 'small', color: color.WHITE_1, bold: true } }} />
          </LanguageBadge>
          {sentenceAudioLink && (
            <SectionHeaderRight>
              <TextBlock
                text='전체문장'
                design={{ text: { size: 'small', color: color.BLACK_4, bold: true } }}
              />
              <TextAudioButton
                sources={[sentenceAudioLink]}
                size='small'
                kind='outlined'
                shape='round'
                width='72px'
              />
            </SectionHeaderRight>
          )}
        </SectionHeader>

        <FieldRow>
          <FieldLabel>
            <TextBlock
              text='빈칸문장'
              design={{ text: { size: 'medium', color: color.BLACK_4, bold: true } }}
            />
          </FieldLabel>
          <FieldValue>
            <TextBlock text={sentenceTemplate || '-'} design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          </FieldValue>
        </FieldRow>

        <FieldRow>
          <FieldLabel>
            <TextBlock
              text='전체문장'
              design={{ text: { size: 'medium', color: color.BLACK_4, bold: true } }}
            />
          </FieldLabel>
          <FieldValue>
            <TextBlock text={sentence || '-'} design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          </FieldValue>
        </FieldRow>

        {choices && choices.length > 0 && (
          <FieldRow>
            <FieldLabel>
              <TextBlock text='보기' design={{ text: { size: 'medium', color: color.BLACK_4, bold: true } }} />
            </FieldLabel>
            <FieldValue>
              <TextBlock
                text={JSON.stringify(choices)}
                design={{ text: { size: 'medium', color: color.BLACK_2 } }}
              />
            </FieldValue>
          </FieldRow>
        )}

        {answers && answers.length > 0 && (
          <FieldRow>
            <FieldLabel>
              <TextBlock text='정답' design={{ text: { size: 'medium', color: color.BLACK_4, bold: true } }} />
            </FieldLabel>
            <FieldValue>
              <TextBlock
                text={JSON.stringify(answers)}
                design={{ text: { size: 'medium', color: color.BROWN_1, bold: true } }}
              />
            </FieldValue>
          </FieldRow>
        )}
      </Section>

      {otherLanguages.some(
        ({ sentenceTemplate: ot, sentence: os, choices: oc, answers: oa }) =>
          ot || os || (oc && oc.length > 0) || (oa && oa.length > 0),
      ) && (
        <Section>
          {otherLanguages.map(
            ({
              label,
              sentenceTemplate: otherTemplate,
              sentence: otherSentence,
              choices: otherChoices,
              answers: otherAnswers,
            }) => {
              const hasTemplate = !!otherTemplate;
              const hasSentence = !!otherSentence;
              const hasChoices = otherChoices && otherChoices.length > 0;
              const hasAnswers = otherAnswers && otherAnswers.length > 0;
              if (!hasTemplate && !hasSentence && !hasChoices && !hasAnswers) return null;
              return (
                <OtherLangBlock key={label}>
                  <SectionHeader>
                    <LanguageBadge>
                      <TextBlock text={label} design={{ text: { size: 'small', color: color.WHITE_1, bold: true } }} />
                    </LanguageBadge>
                  </SectionHeader>
                  {hasTemplate && (
                    <FieldRow>
                      <FieldLabel>
                        <TextBlock
                          text='빈칸문장'
                          design={{ text: { size: 'medium', color: color.BLACK_4, bold: true } }}
                        />
                      </FieldLabel>
                      <FieldValue>
                        <TextBlock text={otherTemplate} design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
                      </FieldValue>
                    </FieldRow>
                  )}
                  {hasSentence && (
                    <FieldRow>
                      <FieldLabel>
                        <TextBlock
                          text='전체문장'
                          design={{ text: { size: 'medium', color: color.BLACK_4, bold: true } }}
                        />
                      </FieldLabel>
                      <FieldValue>
                        <TextBlock text={otherSentence} design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
                      </FieldValue>
                    </FieldRow>
                  )}
                  {hasChoices && (
                    <FieldRow>
                      <FieldLabel>
                        <TextBlock text='보기' design={{ text: { size: 'medium', color: color.BLACK_4, bold: true } }} />
                      </FieldLabel>
                      <FieldValue>
                        <TextBlock
                          text={JSON.stringify(otherChoices)}
                          design={{ text: { size: 'medium', color: color.BLACK_2 } }}
                        />
                      </FieldValue>
                    </FieldRow>
                  )}
                  {hasAnswers && (
                    <FieldRow>
                      <FieldLabel>
                        <TextBlock text='정답' design={{ text: { size: 'medium', color: color.BLACK_4, bold: true } }} />
                      </FieldLabel>
                      <FieldValue>
                        <TextBlock
                          text={JSON.stringify(otherAnswers)}
                          design={{ text: { size: 'medium', color: color.BROWN_1, bold: true } }}
                        />
                      </FieldValue>
                    </FieldRow>
                  )}
                </OtherLangBlock>
              );
            },
          )}
        </Section>
      )}

      <ClozeReviewModal isOpen={isReviewOpen} cloze={selectedCloze} onClose={() => setIsReviewOpen(false)} />
    </Wrapper>
  );
};

export default ClozePreview;
